import { useEffect, useState } from 'react';

import { type AegOccurrence, fetchAegOccurrences } from '../api/aegOccurrences';
import {
    fetchBudgetsByChassis,
    fetchBudgetVersions,
    fetchOccurrencesByChassisForHistory,
    type VehicleHistoryBudget,
    type VehicleHistoryBudgetVersion,
    type VehicleHistoryDispatch,
    type VehicleHistoryOccurrence,
} from '../api/aegVehicleHistory';
import { fetchOccurrencesByChassis, fetchVehicleMaintenanceHistory, type VehicleMaintenanceHistory } from '../api/tas';
import type { IOccurrence } from '../components/support/types';

const PAGE_SIZE = 50;

export type BudgetVersionRow = VehicleHistoryBudgetVersion & { budget: VehicleHistoryBudget };
export type DispatchRow = { dispatch: VehicleHistoryDispatch; occurrence: VehicleHistoryOccurrence };

type SectionKey = 'aeg' | 'budgets' | 'support' | 'revisions' | 'occurrences';

const loadBudgetVersions = async (chassis: string): Promise<BudgetVersionRow[]> => {
    const budgets = await fetchBudgetsByChassis(chassis);
    const versions = await Promise.all(
        budgets.map(async budget => (await fetchBudgetVersions(budget.uuid)).map(version => ({ ...version, budget })))
    );

    return versions
        .flat()
        .sort((a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime());
};

export const useAegVehicleHistory = (chassis?: string) => {
    const [loading, setLoading] = useState(!!chassis);
    const [failed, setFailed] = useState<SectionKey[]>([]);
    const [aeg, setAeg] = useState<AegOccurrence[]>([]);
    const [budgets, setBudgets] = useState<BudgetVersionRow[]>([]);
    const [support, setSupport] = useState<IOccurrence[]>([]);
    const [revisions, setRevisions] = useState<VehicleMaintenanceHistory[]>([]);
    const [occurrences, setOccurrences] = useState<VehicleHistoryOccurrence[]>([]);

    useEffect(() => {
        if (!chassis) {
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);

        const sections: [SectionKey, Promise<unknown>, (value: any) => void][] = [
            ['aeg', fetchAegOccurrences({ chassis, size: PAGE_SIZE }), page => setAeg(page.content)],
            ['budgets', loadBudgetVersions(chassis), setBudgets],
            [
                'support',
                fetchOccurrencesByChassis(chassis, { size: PAGE_SIZE, supportType: 'TECHNICAL_SUPPORT' }),
                page => setSupport(page.content),
            ],
            ['revisions', fetchVehicleMaintenanceHistory(chassis), setRevisions],
            ['occurrences', fetchOccurrencesByChassisForHistory(chassis), setOccurrences],
        ];

        // Cada seção carrega e falha de forma independente: um erro não esconde as demais.
        Promise.allSettled(sections.map(([, request]) => request)).then(results => {
            if (cancelled) {
                return;
            }
            const errors: SectionKey[] = [];
            results.forEach((result, index) => {
                const [key, , apply] = sections[index];
                if (result.status === 'fulfilled') {
                    apply(result.value);
                } else {
                    errors.push(key);
                }
            });
            setFailed(errors);
            setLoading(false);
        });

        return () => {
            cancelled = true;
        };
    }, [chassis]);

    const dispatches: DispatchRow[] = occurrences.flatMap(occurrence =>
        (occurrence.dispatches ?? []).map(dispatch => ({ dispatch, occurrence }))
    );

    return { loading, failed, aeg, budgets, support, revisions, occurrences, dispatches };
};
